/**
 * Alert — lightweight Tailwind alert box.
 *
 * Replaces the former reactstrap `Alert` wrapper (2026-06-19). Keeps a minimal,
 * backward-compatible API (`color` + `className` + children + standard div
 * props) so existing call sites (FlashNotification, ErrorBoundary,
 * InventoryDashboard) don't change. Bootstrap-free.
 */
import React from "react";

type AlertColor =
  | "danger"
  | "success"
  | "warning"
  | "info"
  | "primary"
  | "secondary";

const COLOR_STYLES: Record<AlertColor, string> = {
  danger: "tw-bg-rose-50 tw-border-rose-200 tw-text-rose-800",
  success: "tw-bg-emerald-50 tw-border-emerald-200 tw-text-emerald-800",
  warning: "tw-bg-amber-50 tw-border-amber-200 tw-text-amber-800",
  info: "tw-bg-sky-50 tw-border-sky-200 tw-text-sky-800",
  primary: "tw-bg-sky-50 tw-border-sky-200 tw-text-sky-800",
  secondary: "tw-bg-slate-50 tw-border-slate-200 tw-text-slate-800",
};

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Semantic colour. Accepts the legacy reactstrap colour names. */
  color?: string;
}

const Alert: React.FC<AlertProps> = ({
  color = "info",
  className = "",
  children,
  ...props
}) => {
  const tone = COLOR_STYLES[(color as AlertColor)] ?? COLOR_STYLES.info;
  return (
    <div
      role="alert"
      className={`tw-rounded-md tw-border tw-border-solid tw-px-4 tw-py-3 tw-text-sm ${tone} ${className}`}
      {...props}
    >
      {children}
    </div>
  );
};

export default Alert;
